import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CustomerPortal = ({ primary, domainConfig }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [loginForm, setLoginForm] = useState({ phone: '', customer_id: '', tenant_code: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [device, setDevice] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [activeTab, setActiveTab] = useState('status');
  const [wifiEdit, setWifiEdit] = useState({ band: null, pass: '', ssid: '' });
  const [wifiLoading, setWifiLoading] = useState(false);
  const [showPass, setShowPass] = useState({ '2g': false, '5g': false });
  const [kickLoading, setKickLoading] = useState(false);
  const [showKick, setShowKick] = useState(false);
  const [kickPass, setKickPass] = useState('');

  const appName = domainConfig.app_name || 'My Connection';
  const complaintUrl = (() => {
    const tenantCode = domainConfig.tenant_code || device?.tenant?.code || '';
    if (!tenantCode) return 'https://complaint.pdic.support/complaint/PDIC';
    return 'https://complaint.' + window.location.hostname.split('.').slice(-2).join('.') + '/complaint/' + tenantCode.toUpperCase();
  })();

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 5000); };

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const deviceData = localStorage.getItem('customer_device');
    if (token && deviceData) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setDevice(JSON.parse(deviceData));
      setLoggedIn(true);
    }
    setInitializing(false);
  }, []);

  useEffect(() => { if (loggedIn && device) loadDetails(); }, [loggedIn]);

  const handleLogin = async () => {
    if (!loginForm.phone && !loginForm.customer_id) { setLoginError('Please enter your phone number or connection ID'); return; }
    try {
      setLoginLoading(true); setLoginError('');
      const res = await axios.post('/api/auth/customer/login', { tenant_code: domainConfig.tenant_code || loginForm.tenant_code, phone: loginForm.phone, customer_id: loginForm.customer_id });
      const { token, device: dev } = res.data;
      localStorage.setItem('customer_token', token);
      localStorage.setItem('customer_device', JSON.stringify(dev));
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setDevice(dev); setLoggedIn(true);
    } catch (e) { setLoginError(e.response?.data?.message || 'Connection not found. Please check your details.'); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token'); localStorage.removeItem('customer_device');
    delete axios.defaults.headers.common['Authorization'];
    setLoggedIn(false); setDevice(null); setDetails(null);
  };

  const loadDetails = async () => {
    try { setLoading(true); const res = await axios.get('/api/customer/details'); setDetails(res.data.details); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleWifiUpdate = async () => {
    if (!wifiEdit.pass || wifiEdit.pass.length < 8) { showMsg('Password must be at least 8 characters', 'error'); return; }
    try {
      setWifiLoading(true);
      await axios.post('/api/customer/wifi', { band: wifiEdit.band, password: wifiEdit.pass, ssid: wifiEdit.ssid });
      showMsg('WiFi updated! Reconnect with your new password.'); setWifiEdit({ band: null, pass: '', ssid: '' }); loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); } finally { setWifiLoading(false); }
  };

  const handleKickAll = async () => {
    if (!kickPass || kickPass.length < 8) { showMsg('Password must be at least 8 characters', 'error'); return; }
    try {
      setKickLoading(true);
      await axios.post('/api/customer/kick-all', { newPassword: kickPass });
      showMsg('All devices kicked! Reconnect with new password.');
      setShowKick(false); setKickPass(''); loadDetails();
    } catch(e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setKickLoading(false); }
  };

  const handleReboot = async () => {
    if (!window.confirm('Restart your router? You will be offline for 2-3 minutes.')) return;
    try { await axios.post('/api/customer/reboot'); showMsg('Router is restarting. Please wait 2-3 minutes.'); }
    catch (e) { showMsg('Failed to restart. Please try again.', 'error'); }
  };

  


  // LOGIN PAGE
  if (initializing) return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );

  if (!loggedIn) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', display: 'flex', fontFamily: "'Segoe UI', sans-serif", overflow: 'hidden' }}>
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
        input:focus { border-color: ${primary} !important; box-shadow: 0 0 0 3px ${primary}22; }
      `}</style>

      {/* Animated background */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{ position: 'absolute', width: i%2===0?'300px':'200px', height: i%2===0?'300px':'200px', borderRadius: '50%', background: `${primary}08`, border: `1px solid ${primary}15`, top: `${[10,60,30,80,20,70][i]}%`, left: `${[5,80,50,20,70,40][i]}%`, animation: `pulse ${3+i}s ease-in-out infinite`, animationDelay: `${i*0.5}s` }} />
        ))}
      </div>

      {/* Left - Branding */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: '460px', animation: 'fadeUp 0.6s ease' }}>
          {domainConfig.logo_url ? (
            <img src={domainConfig.logo_url} alt="logo" style={{ height: '60px', objectFit: 'contain', marginBottom: '40px' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '40px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: `linear-gradient(135deg, ${primary}, ${primary}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', boxShadow: `0 8px 32px ${primary}44`, animation: 'float 3s ease-in-out infinite' }}>📡</div>
              <div>
                <div style={{ color: 'white', fontWeight: '800', fontSize: '20px', letterSpacing: '-0.5px' }}>{appName}</div>
                <div style={{ color: '#ffffff44', fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase' }}>Customer Portal</div>
              </div>
            </div>
          )}

          <h1 style={{ fontSize: '48px', fontWeight: '900', color: 'white', lineHeight: '1.1', margin: '0 0 20px', letterSpacing: '-2px' }}>
            Your Internet,<br/><span style={{ color: primary }}>Your Control</span>
          </h1>
          <p style={{ color: '#ffffff55', fontSize: '16px', lineHeight: '1.7', marginBottom: '40px' }}>
            Manage your WiFi, monitor your connection, and get support — all from one place, 24/7.
          </p>

          {[
            { icon: '📶', title: 'WiFi Management', desc: 'Change password & SSID instantly' },
            { icon: '🌐', title: 'Connection Status', desc: 'Real-time connection monitoring' },
            { icon: '💻', title: 'Connected Devices', desc: 'See who is on your network' },
            { icon: '🎫', title: 'Quick Support', desc: 'Raise complaints with one click' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px', animation: 'fadeUp 0.6s ease', animationDelay: `${0.1*i}s`, animationFillMode: 'both' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${primary}20`, border: `1px solid ${primary}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{f.icon}</div>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>{f.title}</div>
                <div style={{ color: '#ffffff44', fontSize: '12px' }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right - Login Form */}
      <div style={{ width: '480px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%', background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '40px', boxShadow: '0 40px 80px rgba(0,0,0,0.5)', animation: 'fadeUp 0.5s ease' }}>
          <h2 style={{ color: 'white', fontSize: '24px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.5px' }}>Sign In</h2>
          <p style={{ color: '#ffffff33', fontSize: '13px', margin: '0 0 28px' }}>Enter your registered details to continue</p>

          {loginError && (
            <div style={{ background: '#ff444415', border: '1px solid #ff444430', borderRadius: '12px', padding: '14px 16px', color: '#ff8888', fontSize: '13px', marginBottom: '20px', display: 'flex', gap: '8px' }}>
              <span>⚠️</span><span>{loginError}</span>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '11px', color: '#ffffff44', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', display: 'block', marginBottom: '8px' }}>Mobile Number</label>

            <input type="tel" placeholder="Your registered mobile number"
              value={loginForm.phone} onChange={e => setLoginForm({ ...loginForm, phone: e.target.value })}
              onKeyPress={e => e.key === 'Enter' && handleLogin()}
              style={{ width: '100%', padding: '14px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', fontSize: '15px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s', fontFamily: 'inherit' }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ color: '#ffffff22', fontSize: '12px', whiteSpace: 'nowrap' }}>or use Connection ID</span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          </div>

          <div style={{ marginBottom: '28px' }}>
            <label style={{ fontSize: '11px', color: '#ffffff44', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', display: 'block', marginBottom: '8px' }}>Connection ID / PPPoE Username</label>
            <input type="text" placeholder="e.g. network4051"
              value={loginForm.customer_id} onChange={e => setLoginForm({ ...loginForm, customer_id: e.target.value })}
              onKeyPress={e => e.key === 'Enter' && handleLogin()}
              style={{ width: '100%', padding: '14px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', fontSize: '15px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s', fontFamily: 'monospace' }} />
          </div>

          <button onClick={handleLogin} disabled={loginLoading}
            style={{ width: '100%', padding: '16px', background: loginLoading ? 'rgba(255,255,255,0.08)' : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`, border: 'none', borderRadius: '12px', color: 'white', fontSize: '16px', fontWeight: '700', cursor: loginLoading ? 'not-allowed' : 'pointer', boxShadow: loginLoading ? 'none' : `0 8px 32px ${primary}40`, transition: 'all 0.3s', letterSpacing: '0.3px' }}>
            {loginLoading ? '⏳ Looking up your connection...' : 'Access My Connection →'}
          </button>

          <div style={{ marginTop: '24px', padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
            <div style={{ color: '#ffffff33', fontSize: '12px', marginBottom: '8px' }}>Having trouble connecting?</div>
            <a href={complaintUrl !== '#' ? complaintUrl : 'https://desk.fibralink.co.in'} target="_blank" rel="noreferrer"
              style={{ color: primary, fontSize: '13px', fontWeight: '700', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              🎫 Report a Problem
            </a>
          </div>

          <div style={{ marginTop: '20px', textAlign: 'center', color: '#ffffff15', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            © {new Date().getFullYear()} {appName} · All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );

  // DASHBOARD

  const isOnline = details?.wan?.status === 'Connected' || details?.wan?.ip || details?.wan?.bytes_received > 0;
  const connectionStatus = loading ? 'Checking...' : (details?.wan?.status || (isOnline ? 'Connected' : 'Offline'));
  const connectionColor = loading ? '#10b981' : (isOnline ? '#10b981' : '#ef4444');

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse2 { 0%,100%{opacity:1} 50%{opacity:0.5} }
        * { box-sizing: border-box; }
      `}</style>

      {/* Header */}
      <div style={{ background: '#0f172a', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 20px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {domainConfig.logo_url ? (
              <img src={domainConfig.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain' }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '22px' }}>📡</span>
                <span style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>{appName}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>{device?.customer_name || 'My Account'}</div>
              <div style={{ color: '#ffffff44', fontSize: '11px', fontFamily: 'monospace' }}>{device?.serial || ''}</div>
            </div>
            <button onClick={handleLogout} style={{ padding: '7px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#ffffff77', fontSize: '12px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px 20px' }}>

        {/* Status Hero Card */}
        <div style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '20px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', animation: 'slideUp 0.4s ease' }}>
          <div style={{ background: isOnline ? `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` : 'linear-gradient(135deg, #dc2626, #b91c1c)', padding: '28px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isOnline ? '#86efac' : '#fca5a5', animation: 'pulse2 2s infinite' }} />
                  <span style={{ color: 'white', fontWeight: '800', fontSize: '20px' }}>{loading ? 'Checking...' : (isOnline ? 'Connected' : 'Offline')}</span>
                </div>
                {details?.wan?.ip && (
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontFamily: 'monospace', marginBottom: '4px' }}>
                    IP: {details.wan.ip}
                  </div>
                )}
                {details?.wan?.uptime && (
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                    Uptime: {Math.floor(details.wan.uptime/3600)}h {Math.floor((details.wan.uptime%3600)/60)}m
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '40px', marginBottom: '4px' }}>{isOnline ? '🌐' : '❌'}</div>
                
              </div>
            </div>

            

          </div>

          {/* Quick Stats */}
          {details && details.wan && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'white', borderTop: `3px solid ${primary}` }}>
              <div style={{ padding: '16px 20px', borderRight: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>⬇️ Downloaded</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#10b981' }}>
                  {details.wan.bytes_received ? (Number(details.wan.bytes_received)/1073741824).toFixed(2)+' GB' : '—'}
                </div>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>⬆️ Uploaded</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#2563eb' }}>
                  {details.wan.bytes_sent ? (Number(details.wan.bytes_sent)/1048576).toFixed(0)+' MB' : '—'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message */}
        {msg.text && (
          <div style={{ background: msg.type==='error'?'#fef2f2':'#ecfdf5', border: `1px solid ${msg.type==='error'?'#fecaca':'#bbf7d0'}`, color: msg.type==='error'?'#dc2626':'#16a34a', padding: '14px 16px', borderRadius: '12px', marginBottom: '16px', fontSize: '14px', fontWeight: '500', display: 'flex', gap: '8px', animation: 'slideUp 0.2s ease' }}>
            <span>{msg.type==='error'?'⚠️':'✅'}</span><span>{msg.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '20px' }}>
          {[
            { id: 'status', icon: '📊', label: 'Status' },
            { id: 'wifi', icon: '📶', label: 'WiFi' },
            { id: 'devices', icon: '💻', label: 'Devices' }
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '14px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: activeTab===t.id ? primary : 'white', color: activeTab===t.id ? 'white' : '#64748b', fontWeight: activeTab===t.id ? '700' : '500', fontSize: '14px', boxShadow: activeTab===t.id ? `0 4px 16px ${primary}40` : '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', border: activeTab===t.id ? 'none' : '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '20px' }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <div style={{ width: '36px', height: '36px', border: `3px solid ${primary}22`, borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <div>Loading your connection details...</div>
          </div>
        )}

        {/* STATUS TAB */}
        {activeTab==='status' && !loading && details && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            {/* Connection Details */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📋 Connection Details</h3>
              {[
                { label: 'Status', value: connectionStatus, highlight: connectionColor },
                { label: 'IP Address', value: details.wan?.ip, mono: true },
                { label: 'Connection Type', value: details.wan?.connection_type },
                { label: 'Connection ID', value: details.wan?.pppoe_user || device?.customer_id },
                { label: 'Last Connected', value: details.wan?.last_inform ? new Date(details.wan.last_inform).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'}) : null },
              ].filter(r=>r.value).map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>{r.label}</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: r.highlight||'#1e293b', fontFamily: r.mono?'monospace':'inherit' }}>{r.value}</span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <button onClick={handleReboot}
                style={{ padding: '16px', background: 'white', border: '1px solid #f1f5f9', borderRadius: '14px', cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='#fed7aa'}
                onMouseLeave={e => e.currentTarget.style.borderColor='#f1f5f9'}>
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>🔄</div>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a', marginBottom: '2px' }}>Restart Router</div>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>Takes 2-3 minutes</div>
              </button>
              <a href={complaintUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <button style={{ width: '100%', height: '100%', padding: '16px', background: 'white', border: '1px solid #f1f5f9', borderRadius: '14px', cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='#fca5a5'}
                  onMouseLeave={e => e.currentTarget.style.borderColor='#f1f5f9'}>
                  <div style={{ fontSize: '24px', marginBottom: '6px' }}>🎫</div>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a', marginBottom: '2px' }}>Report Issue</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Register a complaint</div>
                </button>
              </a>
            </div>

            {/* Help Section */}
            <div style={{ background: `${primary}08`, border: `1px solid ${primary}20`, borderRadius: '14px', padding: '16px' }}>
              <div style={{ fontWeight: '700', fontSize: '14px', color: primary, marginBottom: '10px' }}>💡 Quick Tips</div>
              {[
                'Restart your router if speed is slow',
                'Keep devices away from walls for better WiFi',
                'Change WiFi password regularly for security',
                'Contact support if issue persists'
              ].map((tip, i) => (
                <div key={i} style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', display: 'flex', gap: '8px' }}>
                  <span style={{ color: primary }}>→</span><span>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WIFI TAB */}
        {activeTab==='wifi' && !loading && details && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            {[
              { band: details.wifi?.ssid_2g === details.wifi?.ssid_5g && details.wifi?.ssid_5g ? 'both' : '2g', label: details.wifi?.ssid_2g === details.wifi?.ssid_5g && details.wifi?.ssid_5g ? 'WiFi (2.4 & 5 GHz)' : '2.4 GHz', desc: details.wifi?.ssid_2g === details.wifi?.ssid_5g && details.wifi?.ssid_5g ? 'Band steering enabled - same SSID' : 'Better range, slower speed', ssid: details.wifi?.ssid_2g, pass: details.wifi?.password_2g, enabled: details.wifi?.enabled_2g },
              ...(details.wifi?.ssid_5g ? [{ band: '5g', label: '5 GHz', desc: 'Faster speed, shorter range', ssid: details.wifi?.ssid_5g, pass: details.wifi?.password_5g, enabled: details.wifi?.enabled_5g }] : [])
            ].map(w => (
              <div key={w.band} style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      <span style={{ fontSize: '18px' }}>📶</span>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{w.label} WiFi</h3>
                      <span style={{ background: w.enabled?'#ecfdf5':'#fef2f2', color: w.enabled?'#10b981':'#ef4444', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                        {w.enabled?'Active':'Off'}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>{w.desc}</div>
                  </div>
                  <button onClick={() => setWifiEdit(wifiEdit.band===w.band?{band:null,pass:'',ssid:''}:{band:w.band,ssid:w.ssid||'',pass:''})}
                    style={{ padding: '8px 16px', background: wifiEdit.band===w.band?'#fef2f2':`${primary}15`, color: wifiEdit.band===w.band?'#dc2626':primary, border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                    {wifiEdit.band===w.band ? '✕ Cancel' : '✏️ Change'}
                  </button>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Network Name</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{w.ssid || 'N/A'}</div>
                    </div>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📡</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</div>
                      <div style={{ fontSize: '15px', fontWeight: '600', fontFamily: 'monospace', color: '#475569' }}>
                        {showPass[w.band] ? (w.pass || '⚠ Password not set') : (w.pass ? '••••••••' : '⚠ Tap Show')}
                      </div>
                    </div>
                    <button onClick={() => setShowPass({...showPass,[w.band]:!showPass[w.band]})}
                      style={{ padding: '8px 14px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                      {showPass[w.band] ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                {wifiEdit.band===w.band && (
                  <div style={{ marginTop: '16px', padding: '18px', background: '#eff6ff', borderRadius: '14px', border: '1px solid #bfdbfe', animation: 'slideUp 0.2s ease' }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1d4ed8', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>Change WiFi Settings</div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: '600', display: 'block', marginBottom: '6px' }}>New Network Name (SSID)</label>
                      <input value={wifiEdit.ssid} onChange={e=>setWifiEdit({...wifiEdit,ssid:e.target.value})} placeholder={w.ssid||'WiFi name'}
                        style={{ width: '100%', padding: '13px', borderRadius: '10px', border: '1px solid #bfdbfe', fontSize: '14px', outline: 'none' }} />
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: '600', display: 'block', marginBottom: '6px' }}>New Password <span style={{ color: '#94a3b8', fontWeight: '400' }}>(min 8 characters)</span></label>
                      <input type="text" value={wifiEdit.pass} onChange={e=>setWifiEdit({...wifiEdit,pass:e.target.value})} placeholder="New WiFi password"
                        style={{ width: '100%', padding: '13px', borderRadius: '10px', border: '1px solid #bfdbfe', fontSize: '14px', outline: 'none', fontFamily: 'monospace' }} />
                    </div>
                    <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '12px', fontSize: '12px', color: '#92400e', marginBottom: '14px', display: 'flex', gap: '8px' }}>
                      <span>⚠️</span><span>All connected devices will be disconnected. Reconnect with your new password.</span>
                    </div>
                    <button onClick={handleWifiUpdate} disabled={wifiLoading||wifiEdit.pass.length<8}
                      style={{ width: '100%', padding: '14px', background: wifiEdit.pass.length>=8?primary:'#e2e8f0', color: wifiEdit.pass.length>=8?'white':'#94a3b8', border: 'none', borderRadius: '10px', cursor: wifiEdit.pass.length>=8?'pointer':'not-allowed', fontWeight: '700', fontSize: '15px', boxShadow: wifiEdit.pass.length>=8?`0 4px 16px ${primary}40`:'none', transition: 'all 0.2s' }}>
                      {wifiLoading?'⏳ Updating WiFi...':'💾 Update WiFi Settings'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* DEVICES TAB */}
        {activeTab==='devices' && !loading && details && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: '0 0 3px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Connected Devices</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{details.lan_hosts ? details.lan_hosts.filter(h=>h.active).length : 0} devices currently active</p>
                </div>
                <div style={{ background: `${primary}15`, color: primary, padding: '8px 14px', borderRadius: '20px', fontSize: '14px', fontWeight: '800' }}>
                  {details.lan_hosts ? details.lan_hosts.filter(h=>h.active).length : 0}
                </div>
              </div>

              {details.lan_hosts?.filter(h=>h.active).length===0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>💻</div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>No Active Devices</div>
                  <div style={{ fontSize: '13px' }}>Connect a device to your WiFi to see it here</div>
                </div>
              ) : (
                details.lan_hosts?.filter(h=>h.active).map((h,i) => {
                  const isPhone = h.hostname?.toLowerCase().match(/phone|iphone|android|samsung|xiaomi|redmi|oppo|vivo|realme/);
                  const isLaptop = h.hostname?.toLowerCase().match(/laptop|pc|desktop|computer|macbook|windows|dell|hp|lenovo/);
                  const isTv = h.hostname?.toLowerCase().match(/tv|television|smart-tv|roku|firetv|chromecast/);
                  const icon = isPhone?'📱':isLaptop?'💻':isTv?'📺':'🔌';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', borderRadius: '12px', background: i%2===0?'#f8fafc':'white', marginBottom: '6px', border: '1px solid #f1f5f9' }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                        {icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', marginBottom: '2px' }}>{h.hostname||'Unknown Device'}</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>{h.ip}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ background: '#ecfdf5', color: '#10b981', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', display: 'block', marginBottom: '4px' }}>● Active</span>
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>{h.mac?.toUpperCase()}</div>
                      </div>
                    </div>
                  );
                })
              )}

              {details.lan_hosts?.filter(h=>!h.active).length>0 && (
                <div style={{ textAlign: 'center', padding: '12px', fontSize: '12px', color: '#94a3b8', borderTop: '1px solid #f1f5f9', marginTop: '8px' }}>
                  {details.lan_hosts.filter(h=>!h.active).length} inactive device(s) not shown
                </div>
              )}
            </div>

            {/* Kick All Devices */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginTop: '14px', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showKick ? '16px' : 0 }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a', marginBottom: '2px' }}>Remove Unknown Devices</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Change WiFi password to kick all devices</div>
                </div>
                <button onClick={() => setShowKick(!showKick)}
                  style={{ padding: '8px 14px', background: showKick ? '#fef2f2' : '#fff7ed', color: showKick ? '#dc2626' : '#ea580c', border: '1px solid ' + (showKick ? '#fecaca' : '#fed7aa'), borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                  {showKick ? 'Cancel' : 'Secure WiFi'}
                </button>
              </div>
              {showKick && (
                <div>
                  <div style={{ background: '#fff7ed', borderRadius: '10px', padding: '12px', fontSize: '13px', color: '#92400e', marginBottom: '14px' }}>
                    All devices will be disconnected. Reconnect with your new password.
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '6px' }}>New WiFi Password (both 2.4G and 5G)</label>
                    <input type="text" value={kickPass} onChange={e => setKickPass(e.target.value)}
                      placeholder="New password min 8 characters"
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' }} />
                  </div>
                  <button onClick={handleKickAll} disabled={kickLoading || kickPass.length < 8}
                    style={{ width: '100%', padding: '13px', background: kickPass.length >= 8 ? '#ea580c' : '#e2e8f0', color: kickPass.length >= 8 ? 'white' : '#94a3b8', border: 'none', borderRadius: '10px', cursor: kickPass.length >= 8 ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '14px' }}>
                    {kickLoading ? 'Removing devices...' : 'Kick All and Set New Password'}
                  </button>
                </div>
              )}
            </div>

            {/* Report Issue CTA */}
            <a href={complaintUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'block', marginTop: '14px' }}>
              <div style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)`, borderRadius: '16px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', boxShadow: `0 8px 24px ${primary}40` }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>🎫</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'white', fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>Having an issue with your connection?</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Click here to register a complaint</div>
                </div>
                <span style={{ color: 'white', fontSize: '20px' }}>→</span>
              </div>
            </a>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '28px', color: '#94a3b8', fontSize: '12px' }}>
          <div style={{ marginBottom: '8px' }}>
            <a href={complaintUrl} target="_blank" rel="noreferrer" style={{ color: primary, fontWeight: '600', textDecoration: 'none', fontSize: '13px' }}>
              🎫 Register Complaint
            </a>
            <span style={{ margin: '0 12px', color: '#e2e8f0' }}>|</span>
            <button onClick={loadDetails} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>🔃 Refresh</button>
          </div>
          <div style={{ textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '11px' }}>
            © {new Date().getFullYear()} {appName} · All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;
